import { SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  ListItem,
  Skeleton,
  Text,
  UnorderedList,
  useOutsideClick,
} from "@chakra-ui/react";
import * as DateFns from "date-fns";
import { useCombobox } from "downshift";
import { useField } from "formik";
import { FormControl } from "formik-chakra-ui";
import _ from "lodash";
import Markdown from "markdown-to-jsx";
import React from "react";
import { RiCloseLine } from "react-icons/ri";
import { useFocus } from "src/hooks/useFocus";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { GET_PARENTS } from "src/scenes/orgAdmin/parents/graphql/queries";
import { GET_STUDENTS } from "src/scenes/orgAdmin/students/graphql/queries";
import * as GQL from "src/types/graphql";
import { getStudentSearchQuery } from "../graphql/utils";
import { Glossary } from "../Text/Glossary";

const PAGE_SIZE = 5;

export type PersonType = "applicant" | "guardian";

interface PersonSelectInputProp {
  name: string;
  organizationId: uuid;
  personType: PersonType;
  placeholder?: string;
  label?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  onSelectedPerson?: (value: GQL.GetStudents_person | null | undefined) => void;
}

// Generic PersonSelectInput component
export function PersonSelectInput({
  name,
  organizationId,
  personType,
  onSelectedPerson,
  placeholder,
  label,
  isDisabled = false,
  isRequired = false,
}: PersonSelectInputProp) {
  const defaultPlaceholder =
    personType === "guardian"
      ? "Search for guardian by name or ID"
      : "Search for student by name or ID";
  const defaultLabel = personType === "guardian" ? "Guardian" : "Student";

  const finalPlaceholder = placeholder || defaultPlaceholder;
  const finalLabel = label || defaultLabel;
  const outsideRef = React.useRef<HTMLDivElement>(null);

  useOutsideClick({
    ref: outsideRef,
    handler: () => closeMenu(),
  });
  const [field, { initialValue }, helper] = useField(name);
  const [hasUserSelected, setHasUserSelected] = React.useState(false);
  const [inputValue, setInputValue] = React.useState<string>("");
  const [applicants, setApplicants] = React.useState<GQL.GetStudents_person[]>(
    []
  );
  const [totalCount, setTotalCount] = React.useState(0);
  const [offset, setOffset] = React.useState(0);
  const [noResults, setNoResults] = React.useState(false);
  const observerRef = React.useRef<HTMLDivElement | null>(null);

  const [selectedPerson, setSelectedPerson] = React.useState<
    GQL.GetStudents_person | null | undefined
  >(undefined);

  const searchValue: string = React.useMemo(
    () =>
      inputValue
        ? inputValue
        : !hasUserSelected && initialValue
        ? initialValue
        : "",
    [initialValue, inputValue, hasUserSelected]
  );

  const toFullName = (
    person: GQL.GetStudents_person | null | undefined,
    defaultValue: string = ""
  ) => (person ? `${person.first_name} ${person.last_name}` : defaultValue);

  // Choose the appropriate GraphQL query based on person type
  const query = personType === "guardian" ? GET_PARENTS : GET_STUDENTS;

  const { remoteData } = useRemoteDataQuery<
    GQL.GetStudents | GQL.GetParents,
    GQL.GetStudentsVariables | GQL.GetParentsVariables
  >(query, {
    variables: {
      organizationId: organizationId,
      search: {
        _and: [
          getStudentSearchQuery(searchValue || ""),
          { deleted_at: { _is_null: true } },
          { active: { _eq: true } },
        ],
      },
      limit: PAGE_SIZE,
      offset: offset,
      order_by: [
        { first_name: GQL.order_by.asc },
        { last_name: GQL.order_by.asc },
        { birth_date: GQL.order_by.asc },
      ],
    },
    skip: !searchValue || searchValue.length < 3,
    fetchPolicy: "network-only",
  });

  const fetchMorePersons = () => {
    if (applicants.length < totalCount) {
      setOffset(applicants.length);
    }
  };

  React.useEffect(() => {
    if (remoteData.hasError()) {
      helper.setError(
        personType === "guardian"
          ? "Error finding guardians"
          : "Error finding students"
      );
      setSelectedPerson(null);
      onSelectedPerson?.(null);
      setApplicants([]);
    }
  }, [remoteData, helper, onSelectedPerson, personType]);

  React.useEffect(() => {
    if (
      !remoteData.isLoading() &&
      !remoteData.hasError() &&
      remoteData.hasData()
    ) {
      if (offset === 0) {
        setApplicants(remoteData.data.person);
      } else {
        setApplicants((prev) => _.unionBy(prev, remoteData.data.person, "id"));
      }
      const total = remoteData.data.person_aggregate.totals?.total ?? 0;
      setNoResults(total === 0);
      setTotalCount(remoteData.data.person_aggregate.totals?.total ?? 0);
    }
  }, [remoteData, offset, helper]);

  React.useEffect(() => {
    if (!hasUserSelected && applicants.length > 0) {
      const preSelectedPerson = applicants.find(
        (applicant) => applicant.id === initialValue
      );
      if (preSelectedPerson) {
        setSelectedPerson(preSelectedPerson);
        onSelectedPerson?.(preSelectedPerson);
        setInputValue(toFullName(preSelectedPerson));
        setOffset(0);
      }
    }
  }, [applicants, hasUserSelected, initialValue, onSelectedPerson]);

  // handles fetching more persons when the user scrolls to the bottom of the list
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0]?.isIntersecting &&
          !remoteData.isLoading() &&
          applicants.length > 0
        ) {
          fetchMorePersons();
        }
      },
      { rootMargin: "20px" } // Trigger loading slightly before reaching the bottom
    );

    const current = observerRef.current;

    if (current) {
      observer.observe(current);
    }

    return () => {
      if (current) {
        observer.unobserve(current);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicants]);

  // handle focusing when selected person is cleared
  const [isFocused, setIsFocused] = React.useState(false);
  const [inputRef, setInputFocus] = useFocus();
  React.useEffect(() => {
    if (isFocused) {
      setInputFocus();
    }
  }, [setInputFocus, isFocused]);

  React.useEffect(() => {
    if (inputRef.current) {
      inputRef.current.onblur = () => {
        setIsFocused(false);
      };
    }
  }, [inputRef, isFocused]);

  const resetField = () => {
    helper.setTouched(true, false);
    helper.setValue(initialValue);
    setInputValue(initialValue);
    setApplicants([]);
    setSelectedPerson(null);
    onSelectedPerson?.(null);
    setOffset(0);
    setIsFocused(true);
  };

  const handleChange = (value: string | null) => {
    value = value ?? "";
    if (value === field.value) return;

    setHasUserSelected(true);
    const found =
      applicants.find((applicant) => applicant.id === value) || null;

    setSelectedPerson(found);
    onSelectedPerson?.(found);
    helper.setTouched(true, false); // no validate until setValue()
    helper.setValue(value);
  };

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getLabelProps,
    highlightedIndex,
    getItemProps,
    openMenu,
    closeMenu,
  } = useCombobox<GQL.GetStudents_person>({
    items: applicants,
    defaultHighlightedIndex: 0,
    selectedItem: null,
    itemToString: toFullName,
    onInputValueChange({ inputValue: newValue }) {
      if (newValue !== inputValue) {
        setInputValue(newValue ?? "");
        setOffset(0);
      }
    },
    stateReducer(
      state,
      actionAndChanges
    ): Partial<typeof actionAndChanges.changes> {
      const { changes, type, index } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick: {
          const newIndex =
            index !== undefined && index >= 0
              ? index
              : changes.highlightedIndex !== undefined &&
                changes.highlightedIndex >= 0
              ? changes.highlightedIndex
              : undefined;
          const newSelectedItem =
            newIndex !== undefined ? applicants[newIndex] : null;

          return {
            ...changes,
            selectedItem: newSelectedItem,
          };
        }
        case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem: {
          return {
            ...changes,
          };
        }
        case useCombobox.stateChangeTypes.InputBlur: {
          return changes;
        }
        default:
          return changes;
      }
    },
    onSelectedItemChange({ selectedItem }) {
      if (selectedItem) {
        handleChange(selectedItem.id);
      }
    },
  });

  const openMenuUnlessDisabled = React.useCallback(() => {
    if (!isDisabled) {
      openMenu();
    }
  }, [isDisabled, openMenu]);

  const markdownName = React.useCallback(
    (name: string) => {
      if (!inputValue) return name;

      const escapedInput = inputValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(`(${escapedInput})`, "ig");
      return name.replace(regex, "**$1**");
    },
    [inputValue]
  );

  const renderDropdownItem = React.useCallback(
    (person: GQL.GetStudents_person) => {
      const name = markdownName(toFullName(person));
      return (
        <Flex direction="column" gap="0" width="100%">
          <Flex direction="row" gap="0" width="100%">
            <Text fontSize="md" marginEnd="auto">
              <Markdown>{name}</Markdown>
            </Text>
            {person.birth_date && (
              <Text fontSize="sm">
                Born{" "}
                {DateFns.format(
                  DateFns.parse(person.birth_date, "yyyy-MM-dd", new Date()),
                  "MM/dd/yyyy"
                )}
              </Text>
            )}
          </Flex>
          <Text fontSize="sm" overflow="visible">
            ID: {person.id}
          </Text>
          {person.reference_id && (
            <Text fontSize="sm">Reference ID: {person.reference_id}</Text>
          )}
        </Flex>
      );
    },
    [markdownName]
  );

  const renderLoadingItem = React.useCallback(() => {
    return (
      <Flex direction="column" gap="0" width="100%">
        <Flex direction="row" gap="0" width="100%">
          <Skeleton height="1.25rem" width="60%" marginEnd="auto" />
          <Skeleton height="1rem" width="30%" />
        </Flex>
        <Skeleton height="1rem" width="40%" marginTop="0.5rem" />
      </Flex>
    );
  }, []);

  return (
    <Box position="relative" width="100%" ref={outsideRef}>
      <FormControl name={name} isRequired={isRequired}>
        <FormLabel {...getLabelProps()} htmlFor={name}>
          <Glossary>{finalLabel}</Glossary>
        </FormLabel>
        {selectedPerson ? (
          <Flex
            border="1px solid"
            borderColor="gray.200"
            borderRadius="lg"
            paddingLeft={4}
            paddingRight={3}
            paddingTop={2}
            paddingBottom={2}
            gap={2.5}
            direction="row"
          >
            {renderDropdownItem(selectedPerson)}
            {!isDisabled && (
              <Button
                variant="outline"
                colorScheme="gray"
                size="sm"
                p="0"
                onClick={() => {
                  resetField();
                }}
              >
                <RiCloseLine />
              </Button>
            )}
          </Flex>
        ) : (
          <>
            <InputGroup
              onClick={openMenuUnlessDisabled}
              onFocus={openMenuUnlessDisabled}
              display="flex"
              justifyContent="center"
            >
              <InputLeftElement children={<SearchIcon color="gray.500" />} />
              <Input
                {...getInputProps(
                  {
                    onBlur: (event) => {
                      // Only set touched if user has actually interacted with the field
                      if (hasUserSelected || inputValue.length > 0) {
                        helper.setTouched(true);
                      }
                    },
                    ref: inputRef,
                  },
                  { suppressRefError: true }
                )}
                value={inputValue}
                placeholder={finalPlaceholder}
                isDisabled={isDisabled}
                // Disable native HTML5 validation to prevent "Please fill out this field" message
                required={false}
              />
            </InputGroup>
            <UnorderedList
              {...getMenuProps({}, { suppressRefError: true })}
              display={isOpen ? "block" : "none"}
              position="absolute"
              background="white"
              marginLeft={0}
              zIndex="2000"
              boxShadow="lg"
              width="100%"
              marginTop={1}
              overflow="auto"
              maxHeight="300px"
            >
              {!noResults &&
                !remoteData.isLoading() &&
                applicants.map((applicant, index) => (
                  <ListItem
                    {...getItemProps({ item: applicant, index })}
                    key={applicant.id}
                    backgroundColor={
                      highlightedIndex === index ? "blackAlpha.100" : "white"
                    }
                    listStyleType="none"
                  >
                    <Flex align="center" minHeight="16" padding="4">
                      {renderDropdownItem(applicant)}
                    </Flex>
                  </ListItem>
                ))}
              {!remoteData.isLoading() && noResults && (
                <ListItem textAlign="center" p={3}>
                  <Text color="gray.500" fontSize="md">
                    No results found
                  </Text>
                </ListItem>
              )}
              {remoteData.isLoading() && (
                <ListItem textAlign="center" p={2}>
                  <Flex align="center" minHeight="16" padding="4">
                    {renderLoadingItem()}
                  </Flex>
                </ListItem>
              )}
              <Box ref={observerRef} height="5px" />
            </UnorderedList>
          </>
        )}
      </FormControl>
    </Box>
  );
}
